import { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, doc, setDoc, getDocs } from 'firebase/firestore';

const TimecardSystem = ({ user, employees }) => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [payrollReport, setPayrollReport] = useState([]);
  const [filter, setFilter] = useState({
    startDate: '',
    endDate: '',
    employeeId: ''
  });
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 5; // Number of employees per page

  // Modal State for Viewing Punch Details
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => {
    if (user) {
      fetchTimeEntries();
    }
  }, [user]);

  // Fetch Time Entries
  const fetchTimeEntries = async () => {
    const snapshot = await getDocs(collection(db, 'timeEntries'));
    const entries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setTimeEntries(entries);
  };

  // Filter and Group Time Entries by Employee
  const filteredEntries = timeEntries.filter(entry => {
    const entryDate = entry.clockIn?.toDate();
    const startDate = filter.startDate ? new Date(filter.startDate) : null;
    const endDate = filter.endDate ? new Date(filter.endDate) : null;
    const matchesDate =
      (!startDate || entryDate >= startDate) &&
      (!endDate || entryDate <= endDate);
    const matchesEmployee = !filter.employeeId || entry.employeeId === filter.employeeId;
    return matchesDate && matchesEmployee;
  });

  const groupedEntries = filteredEntries.reduce((acc, entry) => {
    const employeeId = entry.employeeId;
    if (!acc[employeeId]) {
      acc[employeeId] = [];
    }
    acc[employeeId].push(entry);
    return acc;
  }, {});

  // Pagination Logic
  const employeeIds = Object.keys(groupedEntries);
  const totalPages = Math.ceil(employeeIds.length / entriesPerPage);
  const paginatedEmployeeIds = employeeIds.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  // Calculate Summary Metrics
  const calculateSummary = () => {
    let totalHours = 0;
    let totalOvertime = 0;
    let totalPay = 0;
    const exceptions = [];

    filteredEntries.forEach(entry => {
      if (entry.clockIn && entry.clockOut) {
        const hours = (entry.clockOut.toDate() - entry.clockIn.toDate()) / (1000 * 60 * 60);
        totalHours += hours;
        if (totalHours > 40) {
          totalOvertime += Math.min(hours, totalHours - 40);
        }
        const employee = employees.find(emp => emp.employeeId === entry.employeeId);
        const payRate = employee?.payRate || 0;
        totalPay += hours * payRate;
      }
      if (!entry.clockOut) {
        exceptions.push({ entryId: entry.id, type: 'Missed Clock Out' });
      }
    });

    return { totalHours, totalOvertime, totalPay, exceptions };
  };

  const summary = calculateSummary();

  // Generate Payroll Report
  const generatePayrollReport = async () => {
    if (!user || !user.email.endsWith('@kactuskwenchers.com')) {
      alert('Unauthorized');
      return;
    }
    if (!filter.startDate || !filter.endDate) {
      alert('Please select a date range for the report.');
      return;
    }
    const startDate = new Date(filter.startDate);
    const endDate = new Date(filter.endDate);
    const report = [];
    const filteredEmployees = filter.employeeId
      ? employees.filter(emp => emp.employeeId === filter.employeeId)
      : employees;

    for (const employee of filteredEmployees) {
      const employeeEntries = timeEntries.filter(
        entry =>
          entry.employeeId === employee.employeeId &&
          entry.clockIn?.toDate() >= startDate &&
          entry.clockOut?.toDate() <= endDate
      );
      let totalHours = 0;
      let overtimeHours = 0;
      employeeEntries.forEach(entry => {
        if (entry.clockIn && entry.clockOut) {
          const hours = (entry.clockOut.toDate() - entry.clockIn.toDate()) / (1000 * 60 * 60);
          totalHours += hours;
          if (totalHours > 40) {
            overtimeHours += Math.min(hours, totalHours - 40);
          }
        }
      });
      const regularHours = Math.min(totalHours, 40);
      const regularPay = regularHours * employee.payRate;
      const overtimePay = overtimeHours * employee.payRate * 1.5;
      report.push({
        employeeId: employee.employeeId,
        name: employee.name,
        totalHours,
        regularHours,
        overtimeHours,
        totalPay: regularPay + overtimePay
      });
    }
    setPayrollReport(report);
    await setDoc(doc(db, 'payroll', Date.now().toString()), { report, generatedAt: new Date() });
  };

  // Export Timesheets
  const exportTimesheets = () => {
    const csvContent = [
      'Employee ID,Name,Clock In,Clock Out,Hours,Location',
      ...filteredEntries.map(entry => {
        const hours = entry.clockIn && entry.clockOut
          ? ((entry.clockOut.toDate() - entry.clockIn.toDate()) / (1000 * 60 * 60)).toFixed(2)
          : 'N/A';
        const location = entry.location
          ? `${entry.location.latitude},${entry.location.longitude}`
          : 'N/A';
        return `${entry.employeeId},${employees.find(emp => emp.employeeId === entry.employeeId)?.name || 'N/A'},${entry.clockIn?.toDate().toLocaleString() || 'N/A'},${entry.clockOut?.toDate().toLocaleString() || 'N/A'},${hours},${location}`;
      })
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'timesheets.csv';
    link.click();
  };

  // View Punch Details
  const viewDetails = (entry) => {
    setSelectedEntry(entry);
    setShowDetailsModal(true);
  };

  // Toggle Employee Section
  const toggleEmployee = (employeeId) => {
    setExpandedEmployee(expandedEmployee === employeeId ? null : employeeId);
  };

  return (
    <div>
      <h2>Timecard System</h2>
      <div className="summary-section">
        <h3>Summary</h3>
        <p>Total Hours: {summary.totalHours.toFixed(2)}</p>
        <p>Overtime Hours: {summary.totalOvertime.toFixed(2)}</p>
        <p>Total Pay: ${summary.totalPay.toFixed(2)}</p>
        {summary.exceptions.length > 0 && (
          <div className="exceptions">
            <h4>Exceptions</h4>
            <ul>
              {summary.exceptions.map((ex, idx) => (
                <li key={idx}>{ex.type} (Entry ID: {ex.entryId})</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <h3>Time Entries</h3>
      <div className="filter-section">
        <input
          type="date"
          value={filter.startDate}
          onChange={e => setFilter({ ...filter, startDate: e.target.value })}
        />
        <input
          type="date"
          value={filter.endDate}
          onChange={e => setFilter({ ...filter, endDate: e.target.value })}
        />
        <select
          value={filter.employeeId}
          onChange={e => setFilter({ ...filter, employeeId: e.target.value })}
        >
          <option value="">All Employees</option>
          {employees.map(emp => (
            <option key={emp.employeeId} value={emp.employeeId}>{emp.name}</option>
          ))}
        </select>
        <button className="generate-report-button" onClick={generatePayrollReport}>
          Generate Payroll Report
        </button>
        <button className="export-button" onClick={exportTimesheets}>
          Export Timesheets
        </button>
      </div>

      <div className="accordion-container">
        {paginatedEmployeeIds.map(employeeId => {
          const employeeEntries = groupedEntries[employeeId];
          const employee = employees.find(emp => emp.employeeId === employeeId);
          return (
            <div key={employeeId} className="accordion-section">
              <div
                className="accordion-header"
                onClick={() => toggleEmployee(employeeId)}
              >
                <h4>{employee?.name || 'Unknown Employee'} (ID: {employeeId})</h4>
                <span>{expandedEmployee === employeeId ? '−' : '+'}</span>
              </div>
              {expandedEmployee === employeeId && (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Clock In</th>
                        <th>Clock Out</th>
                        <th>Hours</th>
                        <th>Location</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeEntries.map(entry => {
                        const hours = entry.clockIn && entry.clockOut
                          ? ((entry.clockOut.toDate() - entry.clockIn.toDate()) / (1000 * 60 * 60)).toFixed(2)
                          : 'N/A';
                        return (
                          <tr key={entry.id}>
                            <td>{entry.clockIn?.toDate().toLocaleString()}</td>
                            <td>{entry.clockOut?.toDate().toLocaleString() || 'N/A'}</td>
                            <td>{hours}</td>
                            <td>{entry.location ? `${entry.location.latitude}, ${entry.location.longitude}` : 'N/A'}</td>
                            <td>
                              <button className="details-button" onClick={() => viewDetails(entry)}>
                                Details
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      <h3>Payroll Report</h3>
      {payrollReport.length > 0 && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Employee ID</th>
                <th>Name</th>
                <th>Total Hours</th>
                <th>Regular Hours</th>
                <th>Overtime Hours</th>
                <th>Total Pay</th>
              </tr>
            </thead>
            <tbody>
              {payrollReport.map(report => (
                <tr key={report.employeeId}>
                  <td>{report.employeeId}</td>
                  <td>{report.name}</td>
                  <td>{report.totalHours.toFixed(2)}</td>
                  <td>{report.regularHours.toFixed(2)}</td>
                  <td>{report.overtimeHours.toFixed(2)}</td>
                  <td>${report.totalPay.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Punch Details Modal */}
      {showDetailsModal && selectedEntry && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Punch Details</h3>
            <p>Employee ID: {selectedEntry.employeeId}</p>
            <p>Name: {employees.find(emp => emp.employeeId === selectedEntry.employeeId)?.name || 'N/A'}</p>
            <p>Clock In: {selectedEntry.clockIn?.toDate().toLocaleString() || 'N/A'}</p>
            <p>Clock Out: {selectedEntry.clockOut?.toDate().toLocaleString() || 'N/A'}</p>
            <p>Hours: {selectedEntry.clockIn && selectedEntry.clockOut
              ? ((selectedEntry.clockOut.toDate() - selectedEntry.clockIn.toDate()) / (1000 * 60 * 60)).toFixed(2)
              : 'N/A'}</p>
            <p>Location: {selectedEntry.location
              ? `${selectedEntry.location.latitude}, ${selectedEntry.location.longitude}`
              : 'N/A'}</p>
            <p>Status: {selectedEntry.status || 'N/A'}</p>
            <div className="modal-buttons">
              <button className="modal-cancel-button" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimecardSystem;